import { MessageCircle, X } from "lucide-react";
import { type ReactElement, useState } from "react";

import { Button } from "@/common/ui/button";
import { cn } from "@/lib/utils";

import { useChatSession } from "../hooks/useChatSession";
import { ChatInput } from "./ChatInput";
import { ChatMessages } from "./ChatMessages";

function ChatPanel({ onClose }: { onClose: () => void }): ReactElement {
  const { messages, isLoading, isStreaming, error, activeModelName, sendMessage } =
    useChatSession();

  return (
    <div
      className={cn(
        "mb-2 flex flex-col",
        "w-80 h-[28rem]",
        "rounded-lg border border-border bg-background shadow-lg"
      )}
      data-testid="chat-panel"
    >
      <div
        className="flex items-center justify-between border-b border-border px-4 py-3"
        data-testid="chat-header"
      >
        <div>
          <h2 className="text-sm font-semibold">Assistant</h2>
          {activeModelName && (
            <span className="text-xs text-muted-foreground" data-testid="chat-model-label">
              {activeModelName}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close assistant"
          data-testid="chat-close"
          title="Close chat"
          className="h-7 w-7"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <ChatMessages
        messages={messages}
        isLoading={isLoading}
        isStreaming={isStreaming}
        error={error}
      />

      <ChatInput onSend={sendMessage} disabled={isLoading || isStreaming} />
    </div>
  );
}

export function ChatWidget(): ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="fixed bottom-4 right-4 z-50" data-testid="chat-widget">
      {isOpen && <ChatPanel onClose={() => setIsOpen(false)} />}

      <Button
        onClick={() => setIsOpen((prev) => !prev)}
        size="icon"
        className="h-12 w-12 rounded-full shadow-lg"
        data-testid="chat-toggle"
        aria-label={isOpen ? "Close assistant" : "Open assistant"}
        title={isOpen ? "Close assistant" : "Open assistant"}
      >
        {isOpen ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </Button>
    </div>
  );
}
